import { AfterViewInit, Component, HostListener, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { JwtHelperService } from "@auth0/angular-jwt";
import { CookieService } from 'ngx-cookie-service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CookieConsentComponent } from 'src/app/components/cookie-consent/cookie-consent.component';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { TestService } from 'src/app/services/test/test.service';
import { Utils } from 'src/app/services/utils/utils.service';
import { environment } from '../environments/environment';
import { SharedStateService } from './services/shared-state/shared-state.service';
import { tagManagerService } from './services/analytics/tagManager.service';

declare function loadIntegratorTemplate(apiUrl: string): any; // see template-loader.js

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  providers: [CookieService, DialogService, TestService]
})
export class AppComponent implements OnInit, AfterViewInit {
  timeout; // callback to keep track of and cancel timer
  isCookieBannerVisible = false;
  dynamicDialogRef: DynamicDialogRef;

  public constructor(private titleService: Title,
    private sharedStateService: SharedStateService,
    private router: Router,
    private utils: Utils,
    private cookieService: CookieService,
    private dialogService: DialogService,
    private analyticsService: AnalyticsService,
    private testService: TestService,
    private tagManagerService: tagManagerService) {
    this.utils.log('AppComponent.constructor');

    // set the app title
    if (environment.brand == 'audi') {
      this.titleService.setTitle('Audi Freeway Plan Extension');
    } else {
      this.titleService.setTitle('EasyDrive');
    }

    // set the brand specific fav icon
    document.getElementById('brandFavIcon').setAttribute('href', 'assets/images/favicon-' + environment.brand + '.ico');

    // set the brand specific stylesheet
    document.getElementById('brandTheme').setAttribute('href', 'assets/' + environment.brand + '-styles.css');

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        // update the app title
        let title = environment.brand == 'audi' ? 'Audi Freeway Plan Extension' : 'EasyDrive';
        switch (event.urlAfterRedirects) {
          case '/enter-details': {
            title += 'Enter Your Details';
            break;
          }
          case '/show-details': {
            title += ' - Owner Details';
            break;
          }
          case '/select-plan': {
            title += ' - Select Your Plan';
            break;
          }
          case '/view-quote': {
            title += '- Purchase';
            break;
          }
          case '/payment': {
            title += '- Payment';
            break;
          }
          case '/just-browsing/vehicle-details': {
            title += ' - Just Browsing - Vehicle Details';
            break;
          }
          case '/just-browsing/select-plan': {
            title += ' - Just Browsing - Select Plan'
            break;
          }
        }
        this.titleService.setTitle(title);
        this.analyticsService.addPageView(event.urlAfterRedirects);
        // Vimal Bhalodia - 05/06/2023:Added for google Tag Manager
        this.tagManagerService.enableGoogleTagManager();
      }
    });
  }

  ngOnInit(): void {
    this.setTimer();
    this.showCookieConsent();

    //// test to see if we can reach the API
    //this.testService.testAPI()
    //  .subscribe(() => {
    //    // ok, nothing to do
    //  }, error => {
    //    this.utils.log('testAPI', error);
    //    this.router.navigate(['offline']);
    //  });
  }

  ngAfterViewInit(): void {
    // load vwsa integrator template
    if (environment.brand == 'vwsa') {
      const apiUrl = environment.templateDownload ? environment.apiUrl + '/Content/WebTemplate' : 'vwsa-template.html';
      setTimeout(() => { loadIntegratorTemplate(apiUrl); }, 1000); // give the captcha time to load
    }
  }

  // #region cookies and analytics

  showCookieConsent(): void {
    if (environment.brand != 'audi') {
      return;
    }

    //this.cookieService.deleteAll();
    if (this.cookieService.get('ecmp_cookie')) {
      return;
    }

    // show cookie banner
    this.isCookieBannerVisible = true;

    // show cookie consent dialog
    this.dynamicDialogRef = this.dialogService.open(CookieConsentComponent, {
      width: '60%',
      showHeader: false,
      baseZIndex: 10000,
      closable: false,
      closeOnEscape: false
    });

    this.dynamicDialogRef.onClose.subscribe((result: boolean) => {
      if (result) {
        this.analyticsService.addPageView(this.router.url);
      }
      // so we don't show the banner and dialog again
      this.cookieService.set('ecmp_cookie', 'true', 365);
    });
  }

  onCloseCookieBanner(url: string): void {
    this.isCookieBannerVisible = false;

    if (url) {
      window.open(url, '_blank').focus();
    }
  }

  // #endregion

  // #region session timer

  // The following actions reset the timer.
  // When the timer elapses the session expires.
  @HostListener('document:keypress')
  @HostListener('document:mousedown')
  @HostListener('document:touchstart')
  @HostListener('document:scroll')
  setTimer(): void {
    //console.log('setTimer', 'clearing');
    clearTimeout(this.timeout);

    if (this.sharedStateService.ownerInfo) {
      //console.log('checking user token expiration');
      const jwtHelper = new JwtHelperService();
      if (jwtHelper.isTokenExpired(this.sharedStateService.ownerInfo.jwtToken)) {
        if (environment.production) {
          //console.log('user token expired', jwtHelper.getTokenExpirationDate(this.sharedStateService.ownerInfo.jwtToken));
          this.logOut();
        }
      }
    }

    //console.log('setTimer', 'starting');
    this.timeout = setTimeout(() => {
      //console.log('timeout expired');
      this.logOut();
    }, 1000 * 60 * 15); // set to 15 minutes
  }

  logOut(): void {
    //console.log('logging out');
    this.sharedStateService.clearState();
    this.sharedStateService.showStart();
  }

  // #endregion
}
